import React, { useCallback, useRef, useState } from "react";
import { Select } from "antd";
import { useMutations } from "../hooks/useApiMutation";
import useDebounce from "../hooks/useDebounce";

/**
 * Remote select with a debounced search. The mutation hook creates a fresh
 * `mutate` function on every render, so keeping it in a ref stops inline props
 * (`params={{ ... }}`, etc.) from invalidating the fetch callback and
 * triggering a cascade of requests when the parent re-renders.
 */
const SearchSelect = ({
  fetchKey,
  endpoint,
  labelKey = "description",
  valueKey = "id",
  params = {},
  ...selectProps
}) => {
  const { mutate } = useMutations();
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;

  // Params is often passed inline (`params={{ companyId: x }}`) so it gets a
  // new identity each render. Hide that behind a ref so fetchOptions stays stable.
  const paramsRef = useRef(params);
  paramsRef.current = params;

  const [options, setOptions] = useState([]);

  const fetchOptions = useCallback(
    async (search = "") => {
      const { data } = await mutateRef.current(fetchKey, {
        method: "get",
        endpoint,
        params: { ...paramsRef.current, search },
      });
      setOptions(
        data?.data?.map((item) => ({
          value: item[valueKey],
          label: item[labelKey],
        })) || []
      );
    },
    [fetchKey, endpoint, labelKey, valueKey]
  );

  const debouncedFetch = useDebounce(fetchOptions, 500);

  return (
    <Select
      showSearch
      filterOption={false}
      onSearch={debouncedFetch}
      onOpenChange={(open) => open && fetchOptions()}
      options={options}
      {...selectProps}
    />
  );
};

export default SearchSelect;
